import { gql, useQuery } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Text,
  Textarea,
} from '@apollo/orbit';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import * as orgRouteConfig from 'src/app/account/routes';
import { ContactRequestSentModal } from 'src/components/hubspotUpgradeButton/ContactRequestSentModal';
import { HubspotUpgradeButton } from 'src/components/hubspotUpgradeButton/HubspotUpgradeButton';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { PricingLink } from 'src/components/pricingLink/PricingLink';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import type { GraphQLTypes } from 'src/lib/graphqlTypes';

const upgradeModalQuery = gql`
  query UpgradeModalQuery($accountId: ID!) {
    me {
      id
      ... on User {
        email
        fullName
      }
    }
    account(id: $accountId) {
      id
    }
  }
`;

interface Props {
  onClose: () => void;
  isOpen: boolean;
  isDismissible?: boolean;
}

/**
 * Regex pattern for validating international phone numbers:
 * - Optional country code (1-3 digits) with optional + prefix
 * - Optional parentheses around area code
 * - Flexible digit groupings (1-4 digits each)
 * - Optional separators (spaces, dots, or dashes) between groups
 * Examples: +1 (555) 555-5555, 555.555.5555, +44 20 7123 4567
 */
const PHONE_REGEX =
  /^(\+\d{1,3}[\s.-]?)?\(?\d{1,4}\)?[\s.-]?\d{1,4}[\s.-]?\d{1,4}[\s.-]?\d{1,4}$/;

const validatePhone = (phone: string): string | null => {
  if (!phone) return null; // Empty is valid since it's optional
  if (!PHONE_REGEX.test(phone)) {
    return 'Please enter a valid phone number';
  }
  return null;
};

export const UpgradeModal = ({
  isOpen,
  onClose,
  isDismissible = true,
}: Props) => {
  const orgId = useCurrentAccountId()[0] || '';
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const { data } = useQuery<
    GraphQLTypes.UpgradeModalQuery,
    GraphQLTypes.UpgradeModalQueryVariables
  >(upgradeModalQuery, {
    variables: {
      accountId: orgId,
    },
    skip: !orgId,
  });

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const homeLink = orgId ? orgRouteConfig.org.path({ orgId }) : '/';

  const me = data?.me;
  const userId = me?.__typename === 'User' ? me.id : '';
  const userEmail = me?.__typename === 'User' && me.email ? me.email : '';
  const userFullName = me?.__typename === 'User' ? me.fullName : '';

  return orgId ? (
    <>
      <Modal size="xl" isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <PermissionGuard
            accountId={orgId}
            accountPermissions={['canSetConstrainedPlan']}
            fallback={
              <>
                <ModalHeader>
                  <ModalTitle>Restricted area</ModalTitle>
                </ModalHeader>
                <ModalBody>
                  <Text>
                    The permissions of your role do not include access to this
                    area. To obtain access, please contact your org admin.
                  </Text>
                </ModalBody>
                <ModalFooter>
                  <ButtonGroup>
                    <Button as={Link} variant="primary" to={homeLink}>
                      Back to Home
                    </Button>
                  </ButtonGroup>
                </ModalFooter>
              </>
            }
          >
            <>
              <ModalHeader>
                <ModalTitle>Unlock unlimited access to GraphOS</ModalTitle>
              </ModalHeader>
              <ModalBody>
                <Text>
                  Lift restrictions for your organization and allow your team
                  access to additional features of GraphOS.
                </Text>
                <FormControl isRequired>
                  <FormLabel>Details of your request</FormLabel>
                  <Textarea
                    placeholder="What is the primary reason you're seeking a paid plan?"
                    value={message}
                    onChange={(e) => setMessage(e.currentTarget.value)}
                  />
                </FormControl>
                <FormControl isInvalid={!!phoneError}>
                  <FormLabel>Phone number</FormLabel>
                  <Input
                    placeholder="Enter a contact number (optional)"
                    type="tel"
                    value={phone}
                    onChange={(e) => {
                      const value = e.currentTarget.value;
                      setPhone(value);
                      const error = validatePhone(value);
                      setPhoneError(error || '');
                    }}
                  />
                  {phoneError && (
                    <FormErrorMessage>{phoneError}</FormErrorMessage>
                  )}
                </FormControl>
                <Text>
                  Submitting this request will prompt the Apollo team to reach
                  out via email with more information about upgrade options.
                </Text>
              </ModalBody>
              <ModalFooter>
                <PricingLink>Review plans</PricingLink>
                <ButtonGroup>
                  {isDismissible && (
                    <Button variant="secondary" type="button" onClick={onClose}>
                      Close
                    </Button>
                  )}
                  <HubspotUpgradeButton
                    onSuccess={() => {
                      setShowSuccessModal(true);
                      onClose();
                    }}
                    accountId={orgId}
                    userId={userId}
                    userEmail={userEmail}
                    userFullName={userFullName}
                    phone={phone}
                    message={message}
                  >
                    Submit contact request
                  </HubspotUpgradeButton>
                </ButtonGroup>
              </ModalFooter>
            </>
          </PermissionGuard>
        </ModalContent>
      </Modal>
      <ContactRequestSentModal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
        }}
      />
    </>
  ) : null;
};
